// A parent job combined with the jobs that roll up to it (#613).
//
// The server sends each member's RAW model (live, or as of a month) and this file combines
// them with the job tab's own calc: `computeRows` per member, then a merge by cost code. It
// returns the SAME shape the tab already renders (FinancialsWip), so the combined view is the
// tab's own table, KPIs and margins over summed inputs, not a second implementation of them. A
// combined figure is therefore always the sum of figures each job's own tab shows.
//
// HOW THEY ARE SUMMED
//  - Contract, billed, and every cost figure: added across all members. (The parent's contract
//    and each child's own contract both count: the family is one project. Whether a child's
//    contract is separate revenue is stated on screen, so a reader can see what was added.)
//  - Cost to complete: each member's OWN, per cost code, then added. Never recomputed on the
//    merged row: a code over budget on one job and under on another would net out and hide a
//    forecast cost (the default CTC is max(budget - actual, 0) per job).
//  - The merged forecast is therefore an explicit CTC for every code, which is what makes the
//    tab's own `computeRows` land on the sum exactly, and why `jobCompleted` is false here.
import { computeRows, totals, summarize, type WipSummary } from './wip';
import type { FinancialsWip, RollUp, RollUpMember, WipCostRow } from './apiClient';

type Contract = NonNullable<FinancialsWip['contract']>;

/** One member's own figures, for the table that shows what was added. */
export interface MemberFigures {
  projectId: number; jobNumber: string; title: string; role: 'parent' | 'child';
  /** Deleted since the month shown; the month still counts it. */
  deleted: boolean;
  final: boolean | null;
  noEstimate: boolean;
  summary: WipSummary;
  actual: number;
}

/** What the tab needs to describe the combination. */
export interface RollUpMeta {
  members: MemberFigures[];
  missing: RollUp['missing'];
  hidden: number;
  asOf: RollUp['asOf'];
  /** The month's freeze times by job, for a month view. */
  frozenAt: string | null;
}

export type CombinedWip = FinancialsWip & { rollUp: RollUpMeta };

const sumNullable = (vals: Array<number | null | undefined>): number | null => {
  const present = vals.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return present.length ? present.reduce((a, b) => a + b, 0) : null;
};

/** The contracts added field by field. Null only when no member has a contract at all. */
export function combineContracts(members: Array<Pick<RollUpMember, 'model'>>): FinancialsWip['contract'] {
  const cs = members.map((m) => m.model.contract).filter((c): c is Contract => c != null);
  if (!cs.length) return null;
  return {
    original: sumNullable(cs.map((c) => c.original)),
    approvedChange: sumNullable(cs.map((c) => c.approvedChange)),
    pendingChange: sumNullable(cs.map((c) => c.pendingChange)),
    revised: sumNullable(cs.map((c) => c.revised)),
    billed: sumNullable(cs.map((c) => c.billed)),
    syncedAt: cs[0].syncedAt ?? null,
  };
}

/** One member's own summary, by the tab's calc. */
const ownRows = (m: RollUpMember) => computeRows(m.model.rows, {
  ctc: m.model.forecast, budgetAdj: m.model.budgetAdj, releaseRemaining: m.model.jobCompleted === true,
});

export function memberFigures(m: RollUpMember, computed: ReturnType<typeof ownRows> = ownRows(m)): MemberFigures {
  const t = totals(computed);
  return {
    projectId: m.projectId, jobNumber: m.jobNumber, title: m.title, role: m.role,
    deleted: m.deleted === true,
    final: m.final, noEstimate: m.budgetSynced === false,
    summary: summarize(t, m.model.contract, m.model.indirectActual),
    actual: t.actual,
  };
}

const key = (code: string | null) => code ?? '';

export function combineRollUp(r: RollUp): CombinedWip {
  type Merged = { row: WipCostRow; base: number; delta: number; ctc: number };
  const merged = new Map<string, Merged>();
  const loose: WipCostRow[] = [];
  let indirectActual = 0;

  const computedByMember = r.members.map(ownRows);
  r.members.forEach((m, i) => {
    const rows = computedByMember[i];
    indirectActual += Number.isFinite(m.model.indirectActual) ? m.model.indirectActual : 0;
    for (const x of rows) {
      // A line with no cost code cannot be keyed, so it cannot carry an explicit CTC. It stays its
      // own row, per member, and the tab's calc gives it that job's own default. That is exact
      // because a no-code line is GL cost only (a budget is always filed under a cost code, so its
      // budget is $0 and its default CTC is $0 whether or not the job is Completed).
      if (x.costCode == null) {
        loose.push({
          costCode: null, costCodeName: x.costCodeName, costTypeGroup: x.costTypeGroup,
          budgeted: x.budgetBase > 0, budget: x.budgetBase, committed: x.committed ?? 0,
          actual: x.actual ?? 0, indirect: x.indirect ?? 0,
        });
        continue;
      }
      const k = key(x.costCode);
      const cur = merged.get(k);
      if (!cur) {
        merged.set(k, {
          row: {
            costCode: x.costCode, costCodeName: x.costCodeName, costTypeGroup: x.costTypeGroup,
            budgeted: false, budget: 0, committed: 0, actual: 0, indirect: 0,
          },
          base: 0, delta: 0, ctc: 0,
        });
      }
      const g = merged.get(k)!;
      g.base += x.budgetBase;
      g.delta += x.budgetDelta;
      g.ctc += x.ctc;
      g.row.committed += x.committed ?? 0;
      g.row.actual += x.actual ?? 0;
      g.row.indirect += x.indirect ?? 0;
      // The first member to carry the code names it and files it; the parent comes first.
      if (!g.row.costCodeName && x.costCodeName) g.row.costCodeName = x.costCodeName;
    }
  });

  const rows: WipCostRow[] = [];
  const forecast: Record<string, number> = {};
  const budgetAdj: Record<string, number> = {};
  for (const [k, g] of merged) {
    rows.push({ ...g.row, budget: g.base, budgeted: g.base > 0 });
    // Every code carries an explicit CTC (the sum of each job's own), so the tab's calc cannot
    // re-derive a default from the merged row.
    forecast[k] = g.ctc;
    if (g.delta !== 0) budgetAdj[k] = g.delta;
  }
  rows.push(...loose);

  const members = r.members.map((m, i) => memberFigures(m, computedByMember[i]));
  const parent = r.members[0];
  return {
    contract: combineContracts(r.members),
    rows,
    indirectActual,
    // Explicit CTC everywhere: nothing is left for the "release remaining budget" default.
    jobCompleted: false,
    forecast,
    ctcLines: [],
    budgetAdj,
    transferLog: [],
    forecastStamp: null,
    provenance: {
      actualEnabled: r.provenance.actualEnabled,
      budgetEnabled: r.provenance.budgetEnabled,
      actualSyncedAt: r.provenance.actualSyncedAt,
      budgetSyncedAt: r.provenance.budgetSyncedAt,
      committedSyncedAt: r.provenance.committedSyncedAt,
      // One member with no estimate makes the combined estimate partial, and a partial
      // estimate against full cost would read as a fade that is not there.
      budgetSynced: r.members.every((m) => m.budgetSynced),
    },
    rollUp: { members, missing: r.missing, hidden: r.hidden, asOf: r.asOf, frozenAt: parent?.frozenAt ?? null },
  };
}
